import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useTranslation } from 'react-i18next';
import { DLG_BTN_PRIMARY } from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Cierra este diálogo y abre el del otro tipo de cuenta */
  onSwitch?: () => void;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" className="shrink-0">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.72 1.22 9.22 3.22l6.9-6.9C35.9 2.34 30.37 0 24 0 14.64 0 6.47 5.37 2.56 13.18l8.02 6.23C12.67 13.17 17.9 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.1 24.5c0-1.64-.15-3.22-.43-4.75H24v9.02h12.42c-.54 2.9-2.18 5.36-4.62 7.02l7.12 5.54C43.5 37.1 46.1 31.3 46.1 24.5z"
    />
    <path
      fill="#FBBC05"
      d="M10.58 28.41a14.5 14.5 0 010-8.82l-8.02-6.23C.9 16.74 0 20.28 0 24c0 3.72.9 7.26 2.56 10.64l8.02-6.23z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.37 0 11.9-2.1 15.88-5.67l-7.12-5.54c-1.97 1.32-4.5 2.1-8.76 2.1-6.1 0-11.33-3.67-13.42-9.04l-8.02 6.23C6.47 42.63 14.64 48 24 48z"
    />
  </svg>
);

const ClientRegisterChoiceDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSwitch,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Qué es esta cuenta, en términos de lo que el usuario puede hacer con ella
  const ventajas = [
    t('clientRegisterChoiceDialog.benefit1', 'Reserva a cualquier hora, sin llamar'),
    t('clientRegisterChoiceDialog.benefit2', 'Tus citas y recordatorios en un solo sitio'),
    t('clientRegisterChoiceDialog.benefit3', 'Gratis, sin cuotas ni tarjeta'),
  ];

  const contentRef = useRef<HTMLDivElement>(null);

  // El DialogContent compartido bloquea el cierre al pulsar fuera. En vez de
  // cambiarlo en ui/dialog (afectaría a todos los diálogos), se restaura solo
  // aquí con un listener propio. El setTimeout evita que lo dispare el mismo
  // click que ha abierto el diálogo.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const el = contentRef.current;
      if (el && !el.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    const id = window.setTimeout(
      () => document.addEventListener('pointerdown', handlePointerDown),
      0
    );

    return () => {
      window.clearTimeout(id);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open, onOpenChange]);

  const registerWithGoogle = async () => {
    onOpenChange(false);
    localStorage.removeItem('post_login_redirect');
    localStorage.setItem('clientBooking_returnUrl', window.location.pathname);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin.replace(/\/$/, '')}/auth/callback?flow=register-client`,
      },
    });
  };

  const goRegisterNormal = () => {
    onOpenChange(false);
    navigate('/register-client');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          p-4 sm:p-6 overflow-y-auto
        "
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-white">
            {t('clientRegisterChoiceDialog.title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
            {t(
              'clientRegisterChoiceDialog.whatIs',
              'La cuenta de cliente es para reservar: buscas un negocio, eliges servicio y hora, y llevas el seguimiento de tus citas.'
            )}
          </DialogDescription>
        </DialogHeader>

        <ul className="mt-3 space-y-1.5">
          {ventajas.map((v) => (
            <li
              key={v}
              className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
            >
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#288E7F]" />
              <span>{v}</span>
            </li>
          ))}
        </ul>

        <div
          className="rounded-xl border border-gray-200 dark:border-white/10
                     bg-white dark:bg-white/5 shadow-sm
                     p-4 space-y-4 mt-4 text-sm"
        >
          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-3 h-11
                       border-gray-300 dark:border-white/20
                       text-gray-700 dark:text-white
                       dark:bg-white/10
                       hover:bg-gray-100 dark:hover:bg-white/20 transition-colors"
            onClick={registerWithGoogle}
          >
            <GoogleIcon />
            {t('clientRegisterChoiceDialog.continueWithGoogle')}
          </Button>

          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
            <span className="text-[11px] text-gray-400 dark:text-gray-500">
              {t('clientRegisterChoiceDialog.or')}
            </span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
          </div>

          <Button
            type="button"
            onClick={goRegisterNormal}
            className={`${DLG_BTN_PRIMARY} w-full h-11`}
          >
            {t('clientRegisterChoiceDialog.registerWithEmail')}
          </Button>
        </div>

        {onSwitch && (
          <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('clientRegisterChoiceDialog.wrongAccount', '¿Tienes un negocio?')}{' '}
            <button
              type="button"
              onClick={onSwitch}
              className="font-semibold text-[#288E7F] hover:underline"
            >
              {t('clientRegisterChoiceDialog.switchAccount', 'Inclúyelo en Bookoll')}
            </button>
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientRegisterChoiceDialog;